import React, { useState, useRef, useEffect } from 'react';
import { 
  BarChart3, Users, Settings, LogOut, Menu, X, User, Calendar, ChevronDown, 
  Clock, HelpCircle, 
  Building, ClipboardCheck,
  ScrollText,
  Car, Briefcase, Shield
} from 'lucide-react';

// --- ส่วน DropdownMenu ---
const DropdownMenu = ({ title, icon: Icon, items, currentPage, setCurrentPage, setShowMobileMenu }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleItemClick = (pageId) => {
    setCurrentPage(pageId);
    setIsOpen(false);
    if (setShowMobileMenu) {
      setShowMobileMenu(false);
    }
  };

  const isActive = items.some(item => item.id === currentPage);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-blue-100 text-blue-700'
            : 'text-gray-300 hover:text-white hover:bg-gray-700'
        }`}
      >
        <Icon className="h-4 w-4" />
        <span>{title}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1">
            {items.map(item => {
              const ItemIcon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item.id)}
                  className={`w-full text-left flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                    currentPage === item.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <ItemIcon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// --- ส่วน Navigation หลัก ---
const Navigation = ({ user, currentPage, setCurrentPage, logout, showMobileMenu, setShowMobileMenu }) => {

  const userRequestItems = [
    { id: 'permission-leave', label: 'ยื่นขอออก', icon: ClipboardCheck },
    { id: 'leave-request', label: 'ยื่นใบลา', icon: Calendar },
    { id: 'late-request', label: 'ยื่นใบขอมาสาย', icon: Clock },
    { id: 'forgot-scan-request', label: 'ยื่นใบลืมลงเวลา', icon: HelpCircle }
  ];
  
  const staffReportItems = [
    { id: 'my-reports', label: 'รายงานของฉัน', icon: BarChart3 },
    { id: 'daily-report', label: 'รายงานรายวัน', icon: ClipboardCheck },
    { id: 'late-report', label: 'รายงานมาสาย', icon: Clock },
    { id: 'leave-report', label: 'รายงานการลา', icon: Calendar },
    { id: 'official-business-report', label: 'รายงานไปราชการ', icon: Briefcase },
    { id: 'permission-report', label: 'รายงานการขอออก', icon: Car },
    { id: 'summary-report', label: 'รายงานสรุป', icon: BarChart3 }
  ];
  
  const staffManagementItems = [
    { id: 'leave-approval', label: 'จัดการใบลา', icon: CalendarCheck },
    { id: 'official-business', label: 'จัดการไปราชการ', icon: Briefcase },
    { id: 'late-approval', label: 'จัดการใบขอมาสาย', icon: Clock },
    { id: 'forgot-scan-approval', label: 'จัดการใบลืมลงเวลา', icon: HelpCircle },
    { id: 'department-management', label: 'แผนก/กลุ่มสาระฯ', icon: Building },
    { id: 'employees', label: 'บุคลากร', icon: Users },
    { id: 'extra-work-days', label: 'วันทำงานพิเศษ', icon: Briefcase },
    { id: 'holidays', label: 'วันหยุด', icon: Calendar },
    { id: 'settings', label: 'ตั้งค่าระบบ', icon: Settings }
  ];

  const adminManageItems = [
    { id: 'attendance-manager', label: 'เวลาเข้า-ออก', icon: Clock },
    { id: 'employees', label: 'บุคลากร', icon: Users },
    { id: 'department-management', label: 'แผนก/กลุ่มสาระฯ', icon: Building },
    { id: 'extra-work-days', label: 'วันทำงานพิเศษ', icon: Briefcase },
    { id: 'holidays', label: 'วันหยุด', icon: Calendar }
  ];
  
  const adminReportItems = [
    { id: 'daily-report', label: 'รายงานรายวัน', icon: ClipboardCheck },
    { id: 'late-report', label: 'รายงานมาสาย', icon: Clock },
    { id: 'leave-report', label: 'รายงานการลา', icon: Calendar },
    { id: 'permission-report', label: 'รายงานการขอออก', icon: Car },
    { id: 'summary-report', label: 'รายงานสรุป', icon: BarChart3 }
  ];

  // =====================================
  // รายการเมนูสำหรับ Role ต่างๆ
  // =====================================

  const superAdminMenu = [
    { type: 'link', id: 'super-admin-dashboard', label: 'จัดการเครือข่ายโรงเรียน', icon: Shield }
  ];

  const publicMenu = [];

  const userMenu = [
    { type: 'link', id: 'face-scanner', label: 'ปฏิบัติงาน', icon: CameraIcon }, // สร้าง CameraIcon จำลองด้านล่าง
    { type: 'link', id: 'my-profile', label: 'โปรไฟล์', icon: User },
    { type: 'link', id: 'my-reports', label: 'รายงานของฉัน', icon: BarChart3 },
    { type: 'dropdown', title: 'ใบอนุญาต', icon: ClipboardCheck, items: userRequestItems }
  ];

  const staffMenu = [
    { type: 'link', id: 'face-scanner', label: 'ปฏิบัติงาน', icon: CameraIcon },
    { type: 'link', id: 'admin-dashboard', label: 'แดชบอร์ด', icon: BarChart3 },
    { type: 'link', id: 'my-profile', label: 'โปรไฟล์', icon: User },
    { type: 'dropdown', title: 'ใบอนุญาต', icon: ClipboardCheck, items: userRequestItems },
    { type: 'dropdown', title: 'รายงาน', icon: BarChart3, items: staffReportItems },
    { type: 'dropdown', title: 'จัดการ', icon: Settings, items: staffManagementItems },
  ];

  const adminMenu = [
    { type: 'link', id: 'admin-dashboard', label: 'แดชบอร์ด', icon: BarChart3 },
    { type: 'dropdown', title: 'รายงาน', icon: BarChart3, items: adminReportItems },
    { type: 'dropdown', title: 'จัดการ', icon: Settings, items: adminManageItems },
    { type: 'link', id: 'settings', label: 'ตั้งค่าระบบ', icon: Settings },
    { type: 'link', id: 'logs', label: 'ประวัติการใช้งาน', icon: ScrollText }
  ];

  const menuItems = user?.is_super_admin 
    ? superAdminMenu 
    : user?.is_admin 
      ? adminMenu 
      : user?.is_staff 
        ? staffMenu 
        : (user ? userMenu : publicMenu);

  const mobileMenuItems = menuItems.flatMap(item => {
    if (item.type === 'link') {
      return item;
    }
    if (item.type === 'dropdown') {
      return [{ type: 'header', label: item.title }, ...item.items];
    }
    if (!item.type && item.id) {
      return { ...item, type: 'link' };
    }
    return [];
  });

  return (
    <nav 
      className="bg-gray-800 border-b border-gray-700 fixed top-0 left-0 right-0 z-50 shadow-lg"
      style={{ paddingTop: 'calc(env(safe-area-inset-top))' }}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2">
              {/* [✅ แก้ไข] เปลี่ยนไอคอน Camera เป็นรูปภาพโลโก้โรงเรียน */}
              <img 
                src="/smartface/logo192.png" 
                alt="School Logo" 
                className="h-8 w-8 object-contain rounded-full bg-white p-0.5" 
              />
              <span className="text-xl font-bold text-white tracking-tight">SmartFace</span>
            </div>
            
            <div className="hidden lg:flex space-x-6">
              {menuItems.map((item, index) => {
                if (item.type === 'dropdown') {
                  return (
                    <DropdownMenu
                      key={index}
                      title={item.title}
                      icon={item.icon}
                      items={item.items}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      setShowMobileMenu={setShowMobileMenu}
                    />
                  );
                }
                
                const Icon = item.icon;
                if (!Icon) return null;

                return (
                  <button
                    key={item.id || index}
                    onClick={() => setCurrentPage(item.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentPage === item.id 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* [✅ แก้ไข] ซ่อนปุ่มเข้าสู่ระบบ ใช้ user && (...) ดักไว้เลย ถ้ายังไม่ล็อกอินจะไม่โชว์อะไร */}
            {user && (
              <>
                <span className="text-sm text-gray-300 hidden md:block">
                  สวัสดี, {user.first_name} {user.last_name}
                  {user.is_super_admin && <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-bold">Super Admin</span>}
                  {!user.is_super_admin && user.is_admin && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Admin</span>}
                  {!user.is_super_admin && user.is_staff && <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">เจ้าหน้าที่</span>}
                </span>
                <button
                  onClick={logout}
                  className="flex items-center space-x-2 text-gray-300 hover:text-red-400 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:block">ออกจากระบบ</span>
                </button>
              </>
            )}

            {/* แสดงปุ่มเมนูเบอร์เกอร์สำหรับมือถือ (เฉพาะตอนล็อกอินแล้ว) */}
            {user && (
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && user && (
          <div 
            className="fixed left-0 right-0 bottom-0 bg-white lg:hidden overflow-y-auto border-t z-40"
            style={{ 
              top: 'calc(4rem + env(safe-area-inset-top))', 
              paddingBottom: 'env(safe-area-inset-bottom)'   
            }}
          >
            <div className="p-4 space-y-1">
              {mobileMenuItems.map((item, index) => {
                if (!item || !item.id) {
                   if (item && item.type === 'header') {
                     return (
                       <h3
                         key={index}
                         className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-2"
                       >
                         {item.label}
                       </h3>
                     );
                   }
                   return null;
                }

                const Icon = item.icon;
                if (!Icon) return null;

                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentPage === item.id 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    } ${item.type !== 'header' ? 'ml-2' : ''}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Helper Components for Icons
const CalendarCheck = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M8 2v4"/>
    <path d="M16 2v4"/>
    <rect width="18" height="18" x="3" y="4" rx="2"/>
    <path d="M3 10h18"/>
    <path d="m9 16 2 2 4-4"/>
  </svg>
);

const CameraIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
    <circle cx="12" cy="13" r="3"/>
  </svg>
);

export default Navigation;